import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { items } from '../portfolioitems';
import styled from 'styled-components';
import {
  InformationCircleIcon,
  ExternalLinkIcon,
} from '@heroicons/react/outline';
import ThreeDotsWave from './ThreeDotsWave';
import { motion, AnimatePresence } from 'framer-motion';
import Typist from 'react-typist';

import { isMobile } from 'react-device-detect';

const Category = ({ height }) => {
  const { category } = useParams();

  console.log(category);

  const [loading, setLoading] = useState(true);
  const [showDescription, setShowDescription] = useState(null);

  const cacheImages = async (imgArr) => {
    const promises = await imgArr.map((img) => {
      return new Promise((resolve, reject) => {
        const image = new Image();
        img.onLoad = resolve();
        img.onerror = reject();
        image.src = img.image;
      });
    });
    await Promise.all(promises).then(() => setLoading(false));
  };

  useEffect(() => {
    setTimeout(() => {
      cacheImages(items);
    }, 1000);
  }, []);

  const animatedVariant = {
    initial: { opacity: 0, y: 100 },
    animate: { opacity: 1, y: 0 },
  };

  const itemVariant = {
    hidden: { opacity: 0 },
    show: { opacity: 1 },
  };

  return (
    <Container height={height}>
      {loading ? (
        <ThreeDotsWave />
      ) : (
        <div className='pt-10 md:pt-20 space-y-10 md:space-y-20 grid place-items-center w-screen'>
          <motion.h1
            className='md:pl-20 font-bold text-2xl place-self-start tracking-wider'
            animate={{ x: 50, opacity: 1 }}
            initial={{ x: -100, opacity: 0 }}
            transition={{ duration: 1, ease: 'easeInOut' }}
          >
            Jp<span className='text-blue-150'>.Works()</span>
          </motion.h1>
          <div className='max-w-sm h-[60px]'>
            <Typist cursor={{ show: false }}>
              <h4 className='text-center font-medium text-xl'>
                Here are some of my personal{' '}
                <span className='font-bold'>clone</span> projects done during my
                free time
              </h4>
            </Typist>
          </div>
          {items.map((item, i) => {
            if (item.category === category)
              return (
                <motion.div
                  key={i}
                  className='relative'
                  variants={animatedVariant}
                  animate='animate'
                  initial='initial'
                  transition={{ duration: 1, delay: i * 0.5 }}
                >
                  <Thumbnail>
                    <OptionCard className='w-[80vw] h-[45vw] md:w-[60vw] md:h-[35vw]'>
                      <OptionCardCover
                        style={{
                          zIndex: 1,
                          backgroundImage: `url(${item.image})`,
                        }}
                      ></OptionCardCover>
                    </OptionCard>
                  </Thumbnail>
                  <AnimatePresence>
                    {showDescription === i && (
                      <motion.div
                        className='absolute flex items-center justify-center h-full w-full rounded-lg top-0 md:p-20 text-center text-2xl md:text-3xl bg-black bg-opacity-50 z-50 shadow-xl'
                        animate={{
                          opacity: 1,
                          scale: 0.7,
                          transition: { duration: 0.5 },
                        }}
                        initial={{ opacity: 0, scale: 0 }}
                        exit={{ opacity: 0, scale: 0 }}
                      >
                        {item.description}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <div className='absolute flex justify-center items-center rounded-lg bottom-3 left-3 z-10 space-x-4'>
                    <div
                      className='flex items-center rounded-md bg-blue-150 p-3 shadow-lg cursor-pointer space-x-1 hover:bg-blue-250'
                      onClick={() => window.open(item.link)}
                    >
                      <ExternalLinkIcon className='h-5 text-white ' />
                      <h4>Live Demo</h4>
                    </div>
                    <div
                      className='flex items-center rounded-md bg-gray-400 p-3 shadow-lg cursor-pointer space-x-1 hover:bg-gray-500'
                      onClick={() => {
                        if (showDescription !== i) {
                          setShowDescription(i);
                        } else {
                          setShowDescription(null);
                        }
                      }}
                    >
                      <InformationCircleIcon className='h-5 text-white ' />
                      <h4>Details</h4>
                    </div>
                  </div>
                </motion.div>
              );
          })}
        </div>
      )}
    </Container>
  );
};

export default Category;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  min-height: ${(props) => props.height}px;
  background-color: black;
  color: white;
  overflow: hidden;
`;

const Thumbnail = styled.div`
  display: relative;
  height: 100%;
  width: 100%;
  background-color: black;
  margin-top: 2.5rem;
  margin-bottom: 2.5rem;
`;

const OptionCard = styled.div`
  position: relative;
  z-index: 1;
  perspective: 1000px;

  :hover > div {
    transform: rotateX(7deg) translateY(-6px);

    ::after {
      transform: translateY(0%);
    }
  }

  ::before {
    display: block;
    content: '';
    position: absolute;
    top: 5%;
    left: 5%;
    width: 90%;
    height: 90%;
    background: rgba($c_0, 0.5);
    box-shadow: 0 6px 12px 12px rgba(59, 130, 246, 0.4);
    will-change: opacity;
    transform-origin: top center;
    transform: skewX(0.001deg);
    transition: transform 0.35s ease-in-out, opacity 0.5s ease-in-out;
  }
  :hover::before {
    opacity: 0.6;
    transform: rotateX(7deg) translateY(-6px) scale(1.05);
  }
`;
const OptionCardCover = styled.div`
  border-radius: 0.5rem;
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-image: linear-gradient(120deg, #f6d365 0%, #fda085 100%);
  background-size: contain;
  perspective-origin: 50% 50%;
  transform-style: preserve-3d;
  transform-origin: top center;
  will-change: transform;
  transform: skewX(0.001deg);
  transition: transform 0.35s ease-in-out;

  ::after {
    display: block;
    content: '';
    position: absolute;
    z-index: 100;
    top: 0;
    left: 0;
    width: 100%;
    height: 130%;
    background: linear-gradient(
      226deg,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0.4) 35%,
      rgba(255, 255, 255, 0.2) 42%,
      rgba(255, 255, 255, 0) 60%
    );
    transform: translateY(-20%);
    will-change: transform;
    transition: transform 0.65s cubic-bezier(0.18, 0.9, 0.58, 1);
  }
`;
